import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, CheckCircle2, Info, Clock, ArrowUpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import espnowService, {
  type EspNowController, type BridgeRelease, type BridgeUpdateStatus,
} from '../../services/espnowIrrigation';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

/**
 * Push a new bridge build to one XBee laptop site.
 *
 * One site at a time, deliberately. The bridge has no staged rollout: the
 * procedure is to update a single site, wait for it to come back on the new
 * version, and only then move on. A "update all sites" button here would make
 * it trivially easy to take out the whole fleet with one bad build.
 *
 * A CONFIRMED ack means the swap worked and the bridge is restarting itself, so
 * the site goes quiet for up to a minute before reappearing on the new version.
 * If the new build will not start, the laptop's own hourly watchdog rolls it
 * back and the site returns on the OLD version — which is why the reported
 * version below, not the ack, is the thing to trust afterwards.
 */
export default function BridgeUpdatePanel({
  controller,
  onRefreshController,
}: {
  controller: EspNowController;
  onRefreshController?: () => void;
}) {
  const tz = useOrgTimezone();

  const [release, setRelease] = useState<BridgeRelease | null>(null);
  const [status, setStatus] = useState<BridgeUpdateStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [latest, last] = await Promise.all([
        espnowService.getLatestBridgeRelease().catch(() => null),
        espnowService.getBridgeUpdateStatus(controller.controller_id).catch(() => null),
      ]);
      setRelease(latest);
      setStatus(last);
    } finally {
      setLoading(false);
    }
  }, [controller.controller_id]);

  useEffect(() => { load(); }, [load]);

  async function handlePush() {
    setPushing(true);
    setError(null);
    setNotice(null);
    try {
      const queued = await espnowService.updateBridge(controller.controller_id);
      setNotice(`Queued v${queued.version}. The bridge polls about every 15 seconds.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not queue the update'));
    } finally {
      setPushing(false);
    }
  }

  // Only XBee laptop sites self-update. An ESP-NOW master takes firmware over
  // the OTA route instead, so this panel would be a dead control there.
  if (controller.transport !== 'xbee') return null;

  const current = controller.bridge_version;
  const behind = Boolean(release && current && release.version !== current);
  const unknownVersion = !current;

  const toneClasses: Record<string, string> = {
    success: 'border-success-500/40 bg-success-50 text-success-700',
    info: 'border-primary-200 bg-primary-50 text-primary-800',
    error: 'border-error-500/40 bg-error-50 text-error-700',
    pending: 'border-neutral-200 bg-neutral-50 text-neutral-800',
  };

  function ToneIcon({ tone }: { tone: string }) {
    if (tone === 'success') return <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />;
    if (tone === 'error') return <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />;
    if (tone === 'pending') return <Clock className="h-4 w-4 mt-0.5 shrink-0" />;
    return <Info className="h-4 w-4 mt-0.5 shrink-0" />;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <ArrowUpCircle className="h-5 w-5" />
          Bridge software
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm">
            <div>
              <span className="text-neutral-500">Running:</span>{' '}
              {current
                ? <span className="font-mono font-medium text-neutral-900">v{current}</span>
                : <span className="text-neutral-600">not reported yet</span>}
            </div>
            <div className="mt-0.5">
              <span className="text-neutral-500">Latest:</span>{' '}
              {release
                ? <span className="font-mono font-medium text-neutral-900">v{release.version}</span>
                : <span className="text-neutral-600">no build published</span>}
            </div>
          </div>

          <Button
            onClick={handlePush}
            isLoading={pushing}
            disabled={pushing || !release || (!behind && !unknownVersion)}
          >
            {behind ? `Update to v${release?.version}` : 'Push update'}
          </Button>
        </div>

        {behind && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-warning-500/40 bg-warning-50 text-sm text-warning-700">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              This site is behind. Update one site and confirm it comes back before doing the rest.
            </span>
          </div>
        )}

        {unknownVersion && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-neutral-200 bg-neutral-50 text-sm text-neutral-800">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              This site has not reported a bridge version yet. It will appear after the
              laptop next registers.
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-error-500/40 bg-error-50 text-sm text-error-700">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {notice && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-primary-200 bg-primary-50 text-sm text-primary-800">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        {/* Last attempt. The server classifies the bridge's ack detail, because
            only one outcome — a deferral while valves are open — is worth
            retrying, and showing a deferral in red would train admins to ignore
            the row entirely. */}
        {status && (
          <div className="pt-3 border-t border-neutral-200">
            <div className="text-xs font-medium text-neutral-600 mb-2">Last update attempt</div>
            <div className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${toneClasses[status.tone] ?? toneClasses.pending}`}>
              <ToneIcon tone={status.tone} />
              <div className="min-w-0">
                <div className="font-medium">{status.summary}</div>
                <div className="text-xs text-neutral-600 mt-1">
                  {status.version ? `v${status.version} · ` : ''}
                  {status.status}
                  {' · '}
                  {formatDateTimeInTz(status.created_at, tz)}
                </div>
                {status.detail && (
                  <div className="text-xs font-mono text-neutral-600 mt-1 break-all">{status.detail}</div>
                )}
              </div>
            </div>

            {status.retryable && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={handlePush}
                isLoading={pushing}
                disabled={pushing}
              >
                Retry now
              </Button>
            )}

            {status.status === 'CONFIRMED' && onRefreshController && (
              <Button variant="ghost" size="sm" className="mt-2 ml-2" onClick={onRefreshController}>
                Check reported version
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
